import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BookOpen,
  ChevronRight,
  FileSpreadsheet,
  FileText,
  IndianRupee,
  Receipt,
  Scale,
} from 'lucide-react';
import { format } from 'date-fns';
import { TimeIntervalFilter } from '@/components/TimeIntervalFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import { businessReportsApi } from '@/api/businessReports';
import { expensesApi } from '@/api/expenses';

const reportCards = [
  {
    to: '/chartered-accountant/accounting',
    title: 'Accounting',
    description:
      'View double-entry books — journals, trial balance, GL, GST/TDS, bank recon, statements, and CFO KPIs (view-only).',
    icon: BookOpen,
    gradient: 'from-teal-500/20 to-teal-500/5',
    border: 'border-teal-500/30',
  },
  {
    to: '/chartered-accountant/revenue',
    title: 'Revenue reports',
    description: 'Breakdown by OPD, lab, pharma, additional services, IPD and day care with CSV downloads.',
    icon: IndianRupee,
    gradient: 'from-emerald-500/20 to-emerald-500/5',
    border: 'border-emerald-500/30',
  },
  {
    to: '/chartered-accountant/bills',
    title: 'Bill-wise report',
    description: 'All OPD, lab, pharma, additional services, day care, IPD, ICU and separate IPD pharma bills in one list with CSV export.',
    icon: FileText,
    gradient: 'from-blue-500/20 to-blue-500/5',
    border: 'border-blue-500/30',
  },
  {
    to: '/chartered-accountant/expenses',
    title: 'Expense reports',
    description: 'Approved and paid expenses with line-level CSV export.',
    icon: Receipt,
    gradient: 'from-amber-500/20 to-amber-500/5',
    border: 'border-amber-500/30',
  },
  {
    to: '/chartered-accountant/tds',
    title: 'TDS reports',
    description: 'TDS deducted lines — filter paid vs unpaid and download CSV.',
    icon: Scale,
    gradient: 'from-violet-500/20 to-violet-500/5',
    border: 'border-violet-500/30',
  },
  {
    to: '/chartered-accountant/gst',
    title: 'GST reports',
    description: 'GSTR-style summaries — coming in a future release.',
    icon: FileSpreadsheet,
    gradient: 'from-slate-500/20 to-slate-500/5',
    border: 'border-slate-500/30',
    soon: true,
  },
];

export default function CaDashboard() {
  const {
    fromIso,
    toIso,
    selectedInterval,
    setSelectedInterval,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    handleTimeIntervalChange,
  } = useReportDateRange('this_month');

  const { data: analytics, isLoading } = useQuery({
    queryKey: ['ca-dashboard-analytics', fromIso, toIso],
    queryFn: () =>
      businessReportsApi.dashboardAnalytics({
        from: fromIso!,
        to: toIso!,
        granularity: 'month',
      }),
    enabled: !!fromIso && !!toIso,
  });

  const { data: tdsAll } = useQuery({
    queryKey: ['ca-tds-summary', fromIso, toIso],
    queryFn: async () => {
      const [paid, unpaid] = await Promise.all([
        expensesApi.listTdsStatus({ from: fromIso, to: toIso, tdsPaid: 'true' }),
        expensesApi.listTdsStatus({ from: fromIso, to: toIso, tdsPaid: 'false' }),
      ]);
      return {
        paid: paid.data?.data?.length ?? 0,
        unpaid: unpaid.data?.data?.length ?? 0,
      };
    },
    enabled: !!fromIso && !!toIso,
  });

  const revenueTotal = analytics?.revenue?.totals?.total ?? 0;

  const summaryCards = [
    { label: 'Total revenue', value: revenueTotal, format: 'currency' as const },
    { label: 'Total expenses', value: analytics?.expenses?.totals?.total ?? 0, format: 'currency' as const },
    { label: 'TDS unpaid', value: tdsAll?.unpaid ?? 0, format: 'integer' as const },
    { label: 'TDS paid', value: tdsAll?.paid ?? 0, format: 'integer' as const },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-white">Financial overview</h2>
        <p className="text-slate-400">
          View and download revenue, expense, and TDS reports for your selected period.
        </p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <TimeIntervalFilter
          onTimeIntervalChange={handleTimeIntervalChange}
          selectedInterval={selectedInterval}
          onIntervalChange={setSelectedInterval}
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
        />
        {fromIso && toIso && (
          <p className="mt-3 text-xs text-slate-500">
            Period: {format(new Date(fromIso), 'dd MMM yyyy')} — {format(new Date(toIso), 'dd MMM yyyy')}
          </p>
        )}
      </div>

      {isLoading ? (
        <p className="text-slate-400">Loading summary…</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {summaryCards.map((c) => (
            <div
              key={c.label}
              className="rounded-2xl border border-white/10 bg-white/5 p-5"
            >
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{c.label}</p>
              <p className="mt-2 text-2xl font-bold tabular-nums text-white">
                {c.format === 'currency'
                  ? `₹${c.value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                  : c.value.toLocaleString('en-IN')}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {reportCards.map((card) => (
          <Link
            key={card.to}
            to={card.to}
            className={`group flex items-start gap-4 rounded-2xl border ${card.border} bg-gradient-to-br ${card.gradient} p-5 transition hover:scale-[1.01]`}
          >
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white">
              <card.icon className="h-6 w-6" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-white">{card.title}</p>
                {card.soon && (
                  <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-medium uppercase text-slate-300">
                    Soon
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-slate-400">{card.description}</p>
            </div>
            <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-slate-500 group-hover:text-emerald-400" />
          </Link>
        ))}
      </div>
    </div>
  );
}
